import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { leadsAPI } from '../../lib/api';
import { Rocket, PhoneForwarded, Loader2, AlertTriangle, RotateCcw, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';

function Sparkline({ points }) {
  const w = 68, h = 20, pad = 2;
  const max = Math.max(1, ...points.map((p) => p.count));
  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const coords = points.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - (p.count / max) * (h - pad * 2);
    return [x, y];
  });
  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lx, ly] = coords[coords.length - 1] || [0, 0];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible" aria-hidden="true">
      <polyline points={coords.map(([x, y]) => `${x},${y}`).join(' ')} fill="none" className="stroke-emerald-400/40" strokeWidth="1" />
      <path d={line} fill="none" className="stroke-emerald-500" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r="2" className="fill-emerald-500" />
    </svg>
  );
}

export default function EnrollmentBadge() {
  const [series, setSeries] = useState([]);
  const [total7, setTotal7] = useState(0);
  const [today, setToday] = useState(0);
  const [missingCc, setMissingCc] = useState(0);
  const [fixing, setFixing] = useState(false);
  // Backfill preview → select → apply → undo
  const [bfOpen, setBfOpen] = useState(false);
  const [bfLoading, setBfLoading] = useState(false);
  const [bfCandidates, setBfCandidates] = useState([]);
  const [bfSelected, setBfSelected] = useState(() => new Set());
  const [bfRegion, setBfRegion] = useState(null);
  const [lastBatchId, setLastBatchId] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [bfTotalMatching, setBfTotalMatching] = useState(0);
  const [applyAllMatching, setApplyAllMatching] = useState(false);
  const [bfMessage, setBfMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [activeDay, setActiveDay] = useState(null);
  const [dayLeads, setDayLeads] = useState([]);
  const [loadingDay, setLoadingDay] = useState(false);

  const load = useCallback(async () => {
    try {
      const [hist, status] = await Promise.all([
        leadsAPI.enrollmentHistory(7).catch(() => ({ data: { series: [], total: 0 } })),
        api.get('/api/leads/backfill-status').catch(() => ({ data: {} })),
      ]);
      const s = hist?.data?.series || [];
      setSeries(s);
      setTotal7(hist?.data?.total || 0);
      setToday(s.length ? s[s.length - 1].count : 0);
      setMissingCc(status?.data?.phone_missing_country || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openDay = async (date) => {
    setActiveDay(date);
    setLoadingDay(true);
    setDayLeads([]);
    try {
      const { data } = await leadsAPI.enrollmentDayLeads(date);
      setDayLeads(data?.leads || []);
    } catch {
      setDayLeads([]);
    } finally { setLoadingDay(false); }
  };

  const openDrilldown = () => {
    setOpen(true);
    const firstWithCount = [...series].reverse().find((p) => p.count > 0) || series[series.length - 1];
    if (firstWithCount) openDay(firstWithCount.date);
  };

  const openBackfillPreview = async () => {
    setBfOpen(true);
    setBfLoading(true);
    setBfCandidates([]);
    setLastBatchId(null);
    try {
      const { data } = await api.get('/api/leads/backfill-phone-country/preview');
      if (data?.success === false) {
        toast.warning(data.message || 'Set your business country first.');
        setBfOpen(false);
        return;
      }
      const cands = data?.candidates || [];
      setBfCandidates(cands);
      setBfRegion(data?.region || null);
      setBfTotalMatching(data?.total_matching || cands.length);
      setApplyAllMatching(false);
      setBfMessage(data?.message || '');
      // Self-heal: if the preview retired unresolvable numbers, refresh the badge
      // so it drops immediately (no stale "Fix N" nag).
      if ((data?.unresolvable_retired || 0) > 0) { load(); }
      // Pre-select the SAFE (metadata) fixes; leave risky bare-number rewrites unchecked.
      setBfSelected(new Set(cands.filter((c) => !c.risky).map((c) => c.id)));
    } catch (e) {
      toast.error('Could not load preview. Try again.');
      setBfOpen(false);
    } finally { setBfLoading(false); }
  };

  const toggleSelect = (id) => {
    setApplyAllMatching(false);
    setBfSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const safeIds = bfCandidates.filter((c) => !c.risky).map((c) => c.id);
  const allSelected = bfCandidates.length > 0 && bfSelected.size === bfCandidates.length;
  const someSelected = bfSelected.size > 0 && bfSelected.size < bfCandidates.length;
  const toggleSelectAll = () => {
    setApplyAllMatching(false);
    setBfSelected((prev) => (prev.size === bfCandidates.length ? new Set() : new Set(bfCandidates.map((c) => c.id))));
  };
  const selectSafeOnly = () => { setApplyAllMatching(false); setBfSelected(new Set(safeIds)); };
  const clearSelection = () => { setApplyAllMatching(false); setBfSelected(new Set()); };

  const applyBackfill = async () => {
    const ids = Array.from(bfSelected);
    if (!applyAllMatching && !ids.length) { toast.info('Select at least one number to fix.'); return; }
    setFixing(true);
    try {
      const payload = applyAllMatching ? { apply_all: true, only_safe: false } : { lead_ids: ids };
      const { data } = await api.post('/api/leads/backfill-phone-country/apply', payload);
      toast.success(data?.message || `Fixed ${data?.updated || 0} number(s).`);
      setLastBatchId(data?.batch_id || null);
      setBfOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply fixes. Try again.');
    } finally { setFixing(false); }
  };

  const undoBackfill = async () => {
    if (!lastBatchId) return;
    setUndoing(true);
    try {
      const { data } = await api.post('/api/leads/backfill-phone-country/undo', { batch_id: lastBatchId });
      toast.success(data?.message || 'Reverted.');
      setLastBatchId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not undo. Try again.');
    } finally { setUndoing(false); }
  };

  if (total7 <= 0 && missingCc <= 0 && !lastBatchId) return null;

  return (
    <div className="inline-flex items-center gap-2 flex-wrap">
      {total7 > 0 && (
        <button
          type="button"
          onClick={openDrilldown}
          className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800 transition-colors hover:bg-emerald-100 dark:border-emerald-700/60 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-900/40"
          title="Click to see exactly which leads the autonomous engine enrolled"
          data-testid="auto-enrolled-today-badge"
        >
          <Rocket className="h-3.5 w-3.5 shrink-0" />
          <span className="tabular-nums">{today} today</span>
          <span className="hidden sm:block"><Sparkline points={series} /></span>
          <span className="text-emerald-600/80 dark:text-emerald-400/70 tabular-nums">{total7}/7d</span>
        </button>
      )}
      {missingCc > 0 && (
        <Button
          size="sm" variant="outline"
          className="h-7 border-amber-400 text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40 text-xs"
          onClick={openBackfillPreview}
          disabled={fixing}
          data-testid="fix-phone-country-btn"
          title="Preview and fix leads saved with a bare phone number so calls dial the right country"
        >
          {fixing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PhoneForwarded className="h-3.5 w-3.5 mr-1.5" />}
          Fix {missingCc} phone country code{missingCc === 1 ? '' : 's'}
        </Button>
      )}
      {lastBatchId && (
        <Button
          size="sm" variant="ghost"
          className="h-7 text-xs text-muted-foreground hover:text-foreground"
          onClick={undoBackfill}
          disabled={undoing}
          data-testid="undo-phone-backfill-btn"
          title="Revert the last country-code fix batch"
        >
          {undoing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
          Undo last fix
        </Button>
      )}

      {/* Backfill preview → per-lead select → apply */}
      <Dialog open={bfOpen} onOpenChange={(o) => { setBfOpen(o); if (!o) load(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="backfill-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneForwarded className="h-4 w-4 text-amber-500" /> Fix phone country codes
            </DialogTitle>
            <DialogDescription>
              Review each change before applying. Safe international (+) numbers only get a country-code tag.
              <span className="text-amber-600 font-medium"> Bare numbers are assumed to be +{bfRegion}</span> — deselect any that belong to another country. You can undo the whole batch afterwards.
            </DialogDescription>
          </DialogHeader>

          {!bfLoading && bfCandidates.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-1 pb-1 shrink-0" data-testid="backfill-bulk-toolbar">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  aria-checked={allSelected ? 'true' : someSelected ? 'mixed' : 'false'}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 accent-primary"
                  data-testid="backfill-select-all"
                />
                Select all ({bfCandidates.length})
              </label>
              <div className="flex items-center gap-1.5">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectSafeOnly} data-testid="backfill-select-safe" title="Select only the safe international (+) numbers">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Safe only ({safeIds.length})
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={clearSelection} data-testid="backfill-clear-selection">
                  Clear
                </Button>
              </div>
            </div>
          )}

          {!bfLoading && bfTotalMatching > bfCandidates.length && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs shrink-0" data-testid="backfill-select-all-matching-banner">
              <span className="text-amber-700 dark:text-amber-300">
                {applyAllMatching
                  ? `All ${bfTotalMatching.toLocaleString()} matching leads will be fixed in one batch.`
                  : `Showing the first ${bfCandidates.length} of ${bfTotalMatching.toLocaleString()} leads that need a country code.`}
              </span>
              {applyAllMatching ? (
                <button
                  type="button"
                  className="font-medium underline text-amber-700 dark:text-amber-300 whitespace-nowrap"
                  onClick={() => { setApplyAllMatching(false); setBfSelected(new Set(bfCandidates.filter((c) => !c.risky).map((c) => c.id))); }}
                  data-testid="backfill-clear-all-matching"
                >
                  Clear
                </button>
              ) : (
                <button
                  type="button"
                  className="font-medium underline text-amber-700 dark:text-amber-300 whitespace-nowrap"
                  onClick={() => { setApplyAllMatching(true); setBfSelected(new Set(bfCandidates.map((c) => c.id))); }}
                  data-testid="backfill-select-all-matching"
                >
                  Select all {bfTotalMatching.toLocaleString()} matching
                </button>
              )}
            </div>
          )}

          <div className="flex-1 overflow-auto min-h-0 rounded-md border divide-y">
            {bfLoading && (
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
              </div>
            )}
            {!bfLoading && bfCandidates.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground" data-testid="backfill-empty-message">
                {bfMessage || 'Nothing to fix — all numbers already have a country code.'}
              </div>
            )}
            {!bfLoading && bfCandidates.map((c) => {
              const checked = bfSelected.has(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-3 p-2.5 text-sm cursor-pointer hover:bg-muted/40"
                  data-testid={`backfill-row-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelect(c.id)}
                    className="h-4 w-4 shrink-0 accent-primary"
                    data-testid={`backfill-check-${c.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      {c.current_phone} <span className="mx-1">→</span>
                      <span className="text-foreground font-medium">{c.proposed_phone}</span>
                      <span className="ml-1">(+{c.proposed_country_code})</span>
                    </div>
                  </div>
                  {c.risky ? (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5" data-testid={`backfill-risky-${c.id}`}>
                      <AlertTriangle className="h-3 w-3" /> Assumed +{bfRegion}
                    </span>
                  ) : (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">
                      <CheckCircle2 className="h-3 w-3" /> Safe
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          <DialogFooter className="shrink-0 flex items-center justify-between sm:justify-between gap-2">
            <span className="text-xs text-muted-foreground mr-auto" data-testid="backfill-selected-count">
              {applyAllMatching
                ? `All ${bfTotalMatching.toLocaleString()} matching selected`
                : `${bfSelected.size} of ${bfCandidates.length} selected`}
            </span>
            <Button variant="outline" size="sm" onClick={() => { setBfOpen(false); load(); }} data-testid="backfill-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={applyBackfill} disabled={fixing || (!applyAllMatching && bfSelected.size === 0)} data-testid="backfill-apply-btn">
              {fixing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              {applyAllMatching ? `Fix all ${bfTotalMatching.toLocaleString()}` : `Fix ${bfSelected.size} selected`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="enrollment-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Rocket className="h-4 w-4 text-emerald-500" /> Auto-enrolled leads</DialogTitle>
            <DialogDescription>Leads the autonomous engine enrolled into outreach — {total7} in the last 7 days.</DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-1.5" data-testid="enrollment-picker">
            {series.map((p) => {
              const day = p.date.slice(5);
              const isActive = activeDay === p.date;
              return (
                <button
                  key={p.date}
                  type="button"
                  onClick={() => openDay(p.date)}
                  disabled={p.count === 0}
                  className={`flex-1 flex flex-col items-center gap-1 rounded-md px-1 py-1.5 text-[10px] transition-colors ${isActive ? 'bg-emerald-100 dark:bg-emerald-900/40' : p.count > 0 ? 'hover:bg-muted' : 'opacity-40'}`}
                  data-testid={`enrollment-day-${p.date}`}
                >
                  <span className={`font-semibold tabular-nums ${p.count > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>{p.count}</span>
                  <span className="text-muted-foreground">{day}</span>
                </button>
              );
            })}
          </div>
          <div className="max-h-72 overflow-auto rounded-md border divide-y">
            {loadingDay && <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>}
            {!loadingDay && dayLeads.length === 0 && <div className="p-3 text-xs text-muted-foreground">No enrollments on this day.</div>}
            {!loadingDay && dayLeads.map((l) => (
              <a
                key={l.id}
                href={`/leads/${l.id}`}
                className="flex items-center justify-between gap-3 p-2.5 text-sm hover:bg-muted/50"
                data-testid={`enrollment-lead-${l.id}`}
              >
                <span className="truncate">
                  <span className="font-medium">{l.name || 'Lead'}</span>
                  {l.company && <span className="ml-1.5 text-xs text-muted-foreground">· {l.company}</span>}
                </span>
                {typeof l.ai_score === 'number' && (
                  <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{l.ai_score}/100</span>
                )}
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
